import { useState } from "react";

import { App } from "antd";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { setAuthenticationState } from "@/redux/authentication/reducer";
import { LocalStorageName } from "@/redux/api";
import { handleErrors } from "@/shared/utils/forms.js";
import { useDeleteUserByIdMutation } from "@/redux/service";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import ConfirmationModal from "@/components_v2/Modal/ConfirmationModal";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import { HookFormInput } from "@/components_v2/FormFields/ReactHookForm/fields";
import { BUTTON_VARIANTS } from "@/components_v2/Button/constants";
import WarningIcon from "@/assets/icons/warning.svg?react";
import DeleteIcon from "@/assets/icons/delete.svg?react";
import { DeleteAccountSectionProps } from "@/features/users/settings/types";

import "../styles.css";

const DeleteAccountSection = ({ userId }: DeleteAccountSectionProps) => {
  const { notification } = App.useApp();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const deleteUserSchema = yup.object().shape({
    password: yup
      .string()
      .typeError(t("users.validation.passwordNotValid"))
      .min(8, t("users.validation.passwordLengthValidation"))
      .matches(/[A-Z]/, t("users.validation.passwordCapitalLetterValidation"))
      .matches(/[0-9]/, t("users.validation.passwordNumberValidation"))
      .required(t("users.validation.passwordRequired")),
  });

  const form = useForm({
    resolver: yupResolver(deleteUserSchema),
    mode: "onChange",
  });
  const [deleteUser] = useDeleteUserByIdMutation();
  const [open, setOpen] = useState(false);

  const handleDelete = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;
    deleteUser({ id: userId, data: form.getValues() })
      .unwrap()
      .then(() => {
        notification.success({ message: t("users.labels.accountDeleted") });
        dispatch(setAuthenticationState({ isAuthenticated: false }));
        window.localStorage.removeItem(LocalStorageName);
        navigate("/login");
      })
      .catch((errors) => {
        const error = errors?.data?.errors[0];
        if (error?.code === "delete_last_user") {
          notification.error({ message: t(`users.errors.${error.code}`) });
        } else {
          handleErrors(errors, form.setError, notification);
        }
      });
  };

  return (
    <div className="user-settings-card flex flex-col gap-200">
      <div className="flex items-center gap-200">
        <BaseSvgRenderer component={WarningIcon} width="20" height="20" />
        <h3 className="header-m-medium">{t("users.labels.deleteAccount")}</h3>
      </div>

      <p className="body-m-regular">
        {t("users.labels.deleteAccountDescription")}
      </p>

      <BaseButton
        className="self-start"
        variant={BUTTON_VARIANTS.DANGER}
        leadingIcon={
          <BaseSvgRenderer
            component={DeleteIcon}
            width="20"
            height="20"
            stroke="white"
          />
        }
        onClick={() => setOpen(true)}
      >
        {t("users.labels.deleteAccountAction")}
      </BaseButton>

      <ConfirmationModal
        open={open}
        onCancel={() => setOpen(false)}
        onConfirm={handleDelete}
        title={t("users.labels.deleteUserConfirmationTitle")}
        confirmButtonText={t("users.labels.deleteAccountAction")}
        content={
          <div className="flex flex-col gap-200">
            <p className="body-m-regular">
              {t("users.labels.deleteUserConfirmationContent")}
            </p>
            <FormFieldWrapper label={t("users.labels.password")}>
              <HookFormInput
                name="password"
                type="password"
                control={form.control}
              />
            </FormFieldWrapper>
          </div>
        }
      />
    </div>
  );
};

export default DeleteAccountSection;
